import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Autocomplete,
  TextField,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridRenderEditCellParams,
  GridEditInputCell,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import { ViewToolbar } from '@/components/ViewToolbar';
import { useTranslationManagement, useUpdateTranslationItem } from '@/hooks/core/useTranslationManagement';
import { TranslationItemDto } from '@/services/core/translationManagementService';
import { getErrorMessage } from '@/lib/apiClient';
import { GRID_ROW_HEIGHT } from '@/lib/gridStyles';
import { useGridStatePersistence } from '@/lib/useGridStatePersistence';
import { usePersistedFilters } from '@/hooks/usePersistedFilters';
import { SUPPORTED_LANGUAGES } from '@/i18n';

const _displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
const COMMON_LANGUAGES = SUPPORTED_LANGUAGES.map((code) => ({
  code,
  label: _displayNames.of(code) ?? code,
}));

const CATEGORIES = [
  { value: '', label: 'All categories' },
  { value: 'DeliveryTerms', label: 'Delivery Terms' },
  { value: 'PaymentTerms', label: 'Payment Terms' },
  { value: 'Product', label: 'Product' },
  { value: 'UnitOfMeasure', label: 'Unit of Measure' },
  { value: 'Account', label: 'Account' },
  { value: 'AccountGroup', label: 'Account Group' },
  { value: 'VatCode', label: 'Vat Code' },
];

function rowId(item: TranslationItemDto): string {
  return `${item.category}-${item.entityId}-${item.field}`;
}

export function TranslationManagementView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiRef = useGridApiRef();
  useGridStatePersistence(apiRef, 'Core', 'TranslationManagement');

  const [filterState, setFilterState] = usePersistedFilters('translationManagement', {
    language: '' as string,
    category: '',
    missingOnly: false,
  });
  const language = filterState.language || null;
  const { category, missingOnly } = filterState;

  const [saveError, setSaveError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

  const queryParams = useMemo(
    () => ({
      language: language ?? '',
      category: category || undefined,
      missingOnly,
      skipCount: paginationModel.page * paginationModel.pageSize,
      maxResultCount: paginationModel.pageSize,
    }),
    [language, category, missingOnly, paginationModel]
  );

  const { data, isLoading, isFetching, refetch } = useTranslationManagement(queryParams, !!language);
  const { mutateAsync: updateItem, isPending: isSaving } = useUpdateTranslationItem();

  const rows = useMemo(
    () => (data?.items ?? []).map((item) => ({ ...item, id: rowId(item) })),
    [data]
  );

  const handleRefresh = () => {
    refetch();
  };

  const processRowUpdate = useCallback(
    async (newRow: TranslationItemDto & { id: string }, oldRow: TranslationItemDto & { id: string }) => {
      if (newRow.targetValue === oldRow.targetValue) return oldRow;
      if (!language) return oldRow;

      setSaveError(null);
      try {
        await updateItem({
          language,
          category: newRow.category,
          entityId: newRow.entityId,
          field: newRow.field,
          value: newRow.targetValue || null,
        });
        return newRow;
      } catch (err) {
        setSaveError(getErrorMessage(err));
        return oldRow;
      }
    },
    [language, updateItem]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'category',
        headerName: t('translations.management.category', { defaultValue: 'Category' }),
        width: 150,
        renderCell: (params) => (
          <Chip
            label={CATEGORIES.find((c) => c.value === params.value)?.label ?? params.value}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
        ),
      },
      {
        field: 'entityDisplayName',
        headerName: t('translations.management.entity', { defaultValue: 'Entity' }),
        width: 200,
      },
      {
        field: 'fieldDisplayName',
        headerName: t('translations.management.field', { defaultValue: 'Field' }),
        width: 130,
      },
      {
        field: 'sourceValue',
        headerName: t('translations.management.sourceValue', { defaultValue: 'Source (default)' }),
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: params.value ? 'normal' : 'italic' }}>
            {params.value ?? '—'}
          </Typography>
        ),
      },
      {
        field: 'targetValue',
        headerName: language
          ? t('translations.management.translationFor', { lang: language, defaultValue: `Translation (${language})` })
          : t('translations.management.translation', { defaultValue: 'Translation' }),
        flex: 1,
        minWidth: 200,
        editable: true,
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditInputCell {...params} />
        ),
        renderCell: (params) => {
          const hasValue = !!params.value;
          return (
            <Typography
              variant="body2"
              sx={{
                color: hasValue ? 'text.primary' : 'warning.main',
                fontStyle: hasValue ? 'normal' : 'italic',
              }}
            >
              {params.value ?? t('translations.missing', { defaultValue: 'Missing' })}
            </Typography>
          );
        },
      },
    ],
    [language, t]
  );

  const missingCount = data?.items.filter((i) => !i.targetValue).length ?? 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {/* Standard toolbar */}
      <ViewToolbar onBack={() => {
        if (location.state?.returnTo) { navigate(location.state.returnTo); return; }
        navigate(-1);
      }} onRefresh={handleRefresh} />

      {/* Page content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 2, pb: 2 }}>
        {/* Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 500, flex: 1 }}>
            {t('translations.management.title', { defaultValue: 'Translation Management' })}
          </Typography>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
          <Autocomplete
            value={language}
            onChange={(_, val) => {
              setFilterState(prev => ({ ...prev, language: val ?? '' }));
              setPaginationModel((m) => ({ ...m, page: 0 }));
            }}
            options={COMMON_LANGUAGES.map((l) => l.code)}
            getOptionLabel={(code) => {
              const lang = COMMON_LANGUAGES.find((l) => l.code === code);
              return lang ? `${lang.code} – ${lang.label}` : code;
            }}
            freeSolo
            sx={{ width: 220 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('translations.management.targetLanguage', { defaultValue: 'Target language' })}
                size="small"
                required
              />
            )}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>
              {t('translations.management.category', { defaultValue: 'Category' })}
            </InputLabel>
            <Select
              value={category}
              label={t('translations.management.category', { defaultValue: 'Category' })}
              onChange={(e) => {
                setFilterState(prev => ({ ...prev, category: e.target.value }));
                setPaginationModel((m) => ({ ...m, page: 0 }));
              }}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={missingOnly}
                onChange={(e) => {
                  setFilterState(prev => ({ ...prev, missingOnly: e.target.checked }));
                  setPaginationModel((m) => ({ ...m, page: 0 }));
                }}
                size="small"
              />
            }
            label={t('translations.management.missingOnly', { defaultValue: 'Missing only' })}
          />

          {data && missingCount > 0 && (
            <Chip
              label={`${missingCount} missing`}
              color="warning"
              size="small"
              variant="outlined"
            />
          )}

          {isSaving && <CircularProgress size={20} />}
        </Box>

        {/* Error banner */}
        {saveError && (
          <Alert severity="error" onClose={() => setSaveError(null)} sx={{ mb: 1 }}>
            {saveError}
          </Alert>
        )}

        {/* Grid */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <DataGridPremium
            density="compact"
            rowHeight={GRID_ROW_HEIGHT}
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            loading={isLoading || isFetching}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pagination
            paginationMode="server"
            rowCount={data?.totalCount ?? 0}
            pageSizeOptions={[25, 50, 100]}
            editMode="cell"
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(err) => setSaveError(getErrorMessage(err))}
            disableRowSelectionOnClick
            localeText={{
              noRowsLabel: language
                ? t('translations.management.noRows', { defaultValue: 'No translations found' })
                : t('translations.management.selectLanguage', { defaultValue: 'Select a target language to start translating.' }),
            }}
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              '& .MuiDataGrid-main': {
                flex: 1,
                overflow: 'auto',
              },
              '& .MuiDataGrid-virtualScroller': {
                flex: 1,
                overflow: 'auto',
              },
              '& .MuiDataGrid-cell--editable': {
                cursor: 'text',
                '&:hover': { backgroundColor: 'action.hover' },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
